import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios";
import { Card, Col, Form, Row, Button, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { showConfirm, showError } from "utils/swalHelper";
import CustomerDetailsSection from "./Components/CustomerDetailsSection";
import SiteVisitSection from "./Components/SiteVisitSection";
import ItemDetailsSection, {
  ItemDetail,
} from "./Components/ItemDetailsSection";
import moment from "moment";
import {
  autocompleteDropdownStyles,
  getDisabledInputStyles,
  DISABLED_INPUT_BG,
} from "Constants/Style";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import Colors from "Constants/Color";

interface SiteVisitData {
  id?: string;
  site_visit_date: string;
  staff_name: string;
  location_url: string;
  address: string;
  city: string;
  state: string;
  remarks: string;
}

interface EnquiryFormData {
  enquiry_date: string;
  source_of_enquiry: string;
  customer_name: string;
  customer_id?: string;
  contact_person_name: string;
  contact_person_phone_no: string;
  remark: string;
  site_visit_enabled: boolean;
  site_visits: SiteVisitData[];
  items: ItemDetail[];
  customer_details: any;
}

interface MasterFormProps {
  initialData?: Partial<EnquiryFormData>;
  isEdit?: boolean;
  enquiryId?: string;
  onSubmit: (data: EnquiryFormData) => Promise<void>;
  loading?: boolean;
  itemsOnlyEdit?: boolean; // If true, only items section is editable, all other fields are read-only
  disableSiteVisit?: boolean; // If true, site visit section is disabled
  customerOptions?: any[];
  enquirySourceOptions?: any[];
  categoryOptions?: any[];
  subCategoryOptions?: any[];
  itemOptions?: any[];
  unitOptions?: any[];
  staffOptions?: any[];
  stateOptions?: any[];
}

// Common data for the sources list
const commonSources = [
  "Website",
  "Email",
  "Phone Call",
  "Walk-in",
  "Social Media",
  "Trade Show / Exhibition",
  "Partner / Vendor Referral",
  "Internal Reference",
  "Tender / RFP Portal",
  "Advertisement",
];

const MasterForm: React.FC<MasterFormProps> = ({
  initialData,
  isEdit = false,
  enquiryId,
  onSubmit,
  loading = false,
  itemsOnlyEdit = false,
  disableSiteVisit = false,
}) => {
  const navigate = useNavigate();

  // Get default date (today)
  const getDefaultDate = () => {
    return moment().format("YYYY-MM-DD");
  };

  // Validation schema
  const validationSchema = useMemo(
    () =>
      Yup.object({
        enquiry_date: Yup.string().required("Enquiry date is required"),
        source_of_enquiry: Yup.string().required(
          "Source of enquiry is required"
        ),
        customer_name: Yup.string().required("Customer name is required"),
        contact_person_phone_no: Yup.string()
          .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
          .required("Contact person phone number is required"),
        items: Yup.array().nullable(),
        site_visit_enabled: Yup.boolean(),
        site_visits: Yup.array().test(
          "site-visit-validation",
          "Site visit date and staff name are required when site visit is enabled",
          function (value) {
            const { site_visit_enabled } = this.parent;
            if (!site_visit_enabled) return true;
            if (!value || value.length === 0) return false;
            const siteVisit = value[0];
            return !!(siteVisit?.site_visit_date && siteVisit?.staff_name);
          }
        ),
      }),
    []
  );

  // Initialize Formik
  const formik = useFormik<EnquiryFormData>({
    enableReinitialize: true,
    initialValues: {
      enquiry_date: initialData?.enquiry_date || getDefaultDate(),
      source_of_enquiry: initialData?.source_of_enquiry || "",
      customer_name: initialData?.customer_name || "",
      customer_id: initialData?.customer_id,
      contact_person_name: initialData?.contact_person_name || "",
      contact_person_phone_no: initialData?.contact_person_phone_no || "",
      remark: initialData?.remark || "",
      site_visit_enabled: initialData?.site_visit_enabled || false,
      site_visits: initialData?.site_visits || [],
      items: initialData?.items || [],
      customer_details: initialData?.customer_details || {},
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setErrors, setTouched }) => {
      // ✅ Validate items
      if (!values.items || values.items.length === 0) {
        showError("Please add at least one item");
        setSubmitting(false);
        return;
      }

      try {
        await onSubmit(values);
        // Only proceed if submission was successful
        // If there's an error, it will be caught and handled
      } catch (error: any) {
        // Error is already handled in the parent component (EnquiryCreateForm)
        // Just log it here to prevent unhandled promise rejection
        console.error("Form submission error:", error);
        // Don't show toast here as it's already shown in the parent
        // This prevents the form from refreshing
        // Don't set errors or reset form as it might cause form refresh
        // Just reset the submitting state
      } finally {
        // Always reset submitting state, even on error
        // This prevents the form from being stuck in submitting state
        setSubmitting(false);
      }
    },
  });

  // Dropdown options states
  const [enquirySources, setEnquirySources] = useState<any[]>(commonSources);
  const [customers, setCustomers] = useState<any[]>([]);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);

  // console.log("disableSiteVisit yash: ==> ", disableSiteVisit);
  // console.log("itemsOnlyEdit yash: ==> ", itemsOnlyEdit);
  
  return (
    <PageContainer>
      <FormHeader
        title={
          itemsOnlyEdit
            ? `${enquiryId ? "Edit" : "Add"} Site Visit`
            : `${enquiryId ? "Edit" : "Add"} Enquiry`
        }
        firstLabel={itemsOnlyEdit ? "Site Visit" : "Enquiry"}
        secondLabel={
          itemsOnlyEdit
            ? `${enquiryId ? "Edit" : "Add"} Site Visit`
            : `${enquiryId ? "Edit" : "Add"} Enquiry`
        }
        handleArrowClick={() =>
          navigate(itemsOnlyEdit ? "/sitevisit" : "/enquiry")
        }
        handleBreadcrumbClick={() =>
          navigate(itemsOnlyEdit ? "/sitevisit" : "/enquiry")
        }
      />
      <hr />
      {loading ? (
        <LoadingSpinner />
      ) : (
        <FormikProvider value={formik}>
          <Form
            onSubmit={(e) => {
              // Explicitly prevent default form submission
              e.preventDefault();
              e.stopPropagation();
              // Call Formik's handleSubmit which will call our onSubmit handler
              formik.handleSubmit(e);
              // Return false as additional safeguard
              return false;
            }}
            noValidate
          >
            {/* Enquiry Details Section */}
            <Card
              style={{
                backgroundColor: `${Colors?.background_color}`,
                padding: "0 10px",
                borderRadius: "8px",
                border: `1px solid ${Colors?.background_color}`,
              }}
            >
              <Card.Body style={{ padding: "0" }}>
                <Row className="gy-3 mb-0 mt-2">
                  <Col xs={12} sm={12} lg={6} className="mb-3">
                    {/* <h6 className="card-title mb-3">Enquiry Details</h6> */}
                    <Row>
                      <Col xs={12} sm={12} lg={6} className="mb-3">
                        <Form.Label className="fw-semibold">
                          Enquiry Date <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="date"
                            name="enquiry_date"
                            value={formik.values.enquiry_date}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={itemsOnlyEdit}
                            isInvalid={
                              !!formik.errors.enquiry_date &&
                              formik.touched.enquiry_date
                            }
                            style={{
                              backgroundColor: itemsOnlyEdit
                                ? DISABLED_INPUT_BG
                                : undefined,
                            }}
                          />
                        </InputGroup>
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.enquiry_date}
                        </Form.Control.Feedback>
                      </Col>

                      <Col xs={12} sm={12} lg={6} className="mb-3">
                        <Form.Label className="fw-semibold">
                          Source of Enquiry{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Autocomplete
                          options={enquirySources}
                          disabled={itemsOnlyEdit}
                          getOptionLabel={(option: any) =>
                            typeof option === "string" ? option : String(option)
                          }
                          value={
                            formik.values.source_of_enquiry
                              ? enquirySources.find(
                                  (source) =>
                                    source === formik.values.source_of_enquiry
                                ) || null
                              : null
                          }
                          onChange={async (event, newValue: any) => {
                            // Since enquirySources is an array of strings, newValue will be a string
                            const value =
                              typeof newValue === "string"
                                ? newValue
                                : newValue?.source_of_enquiry ||
                                  newValue?.name ||
                                  "";
                            await formik.setFieldValue(
                              "source_of_enquiry",
                              value
                            );
                            await formik.setFieldTouched(
                              "source_of_enquiry",
                              true
                            );
                            // Validate the field after setting the value
                            formik.validateField("source_of_enquiry");
                          }}
                          onBlur={() =>
                            formik.setFieldTouched("source_of_enquiry", true)
                          }
                          ListboxProps={autocompleteDropdownStyles.ListboxProps}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) =>
                                typeof opt === "string" ? opt : String(opt)
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                itemsOnlyEdit
                                  ? ""
                                  : "Search from Enquiry Source"
                              }
                              variant="outlined"
                              size="small"
                              error={
                                !!formik.errors.source_of_enquiry &&
                                formik.touched.source_of_enquiry
                              }
                              helperText={
                                formik.touched.source_of_enquiry
                                  ? formik.errors.source_of_enquiry
                                  : ""
                              }
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                ...getDisabledInputStyles(itemsOnlyEdit),
                              }}
                            />
                          )}
                        />
                      </Col>
                    </Row>

                    <Row className="gy-3">
                      <Col xs={12} sm={12} lg={12} className="mb-3">
                        <Form.Label className="fw-semibold">Remark</Form.Label>
                        <Form.Control
                          type="text"
                          name="remark"
                          value={formik.values.remark}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={itemsOnlyEdit}
                          placeholder={itemsOnlyEdit ? "" : "Enter"}
                          style={{
                            backgroundColor: itemsOnlyEdit
                              ? DISABLED_INPUT_BG
                              : undefined,
                          }}
                        />
                      </Col>
                    </Row>
                  </Col>

                  <Col xs={12} sm={12} lg={6} className="mb-3">
                    {/* Customer Details Section */}
                    <CustomerDetailsSection
                      customerOptions={customers}
                      disabled={itemsOnlyEdit}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <hr />



            {/* Site Visit Section */}
            <SiteVisitSection
              staffOptions={staffList}
              stateOptions={states}
              disabled={itemsOnlyEdit || disableSiteVisit}
            />

            <hr />

            {/* Item Details Section */}
            <ItemDetailsSection />

            {/* Submit Buttons */}
            <div className="d-flex justify-content-center gap-3 py-3">
              <CancelBackButton
                label="Cancel"
                onClick={() => {
                  showConfirm(
                    "Any unsaved changes will be lost.",
                    "Are you sure?",
                    "Yes",
                    "No"
                  ).then((result) => {
                    if (result.isConfirmed) {
                      if (itemsOnlyEdit) navigate("/sitevisit");
                      else navigate("/enquiry");
                    }
                  });
                }}
                disabled={formik.isSubmitting}
              />
              <SubmitButton
                isSubmitting={formik.isSubmitting}
                isEdit={isEdit}
                onClick={(e) => {
                  // Prevent any default button behavior
                  if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  // Trigger form submission through Formik
                  formik.handleSubmit();
                }}
                labels={{
                  create: "Submit",
                  update: "Update",
                  submitting: "Submitting...",
                  updating: "Updating...",
                }}
              />
            </div>
          </Form>
        </FormikProvider>
      )}
    </PageContainer>
  );
};

export default MasterForm;
